import { useEffect, useState } from "react";
import {
    Plus, Minus, AlertCircle, Infinity as InfIcon,
    PackageOpen, Package, ShieldOff, ShieldCheck,
} from "lucide-react";
import { api, inr } from "../../lib/api";
import { toast } from "sonner";
import ProductAvailabilityToggle from "../../components/ProductAvailabilityToggle";

export default function MerchantInventory() {
    const [rests, setRests] = useState([]);
    const [rid, setRid] = useState("");
    const [data, setData] = useState({ items: [], in_stock: 0, out_stock: 0, low_stock: 0 });

    useEffect(() => {
        api.get("/merchant/restaurants").then(({ data }) => {
            setRests(data);
            if (data?.[0]) setRid(data[0].id);
        });
    }, []);

    const load = async (r = rid) => {
        if (!r) return;
        const { data } = await api.get(`/merchant/inventory?restaurant_id=${r}`);
        setData(data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [rid]);

    const setStock = async (pid, stock) => {
        try {
            await api.patch(`/merchant/inventory/${pid}/stock?stock=${stock}&reason=MANUAL`);
            toast.success(stock === -1 ? "Set to unlimited" : `Stock set to ${stock}`);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    const adjust = async (pid, delta) => {
        try {
            await api.patch(`/merchant/inventory/${pid}/adjust?delta=${delta}&reason=MANUAL`);
            load();
        } catch (e) {
            const msg = e?.response?.data?.detail || "Failed";
            if (msg.includes("unlimited")) {
                toast.error("Switch to a finite count first using 'Set stock'.");
            } else { toast.error(msg); }
        }
    };
    const setExact = async (pid) => {
        const v = window.prompt("Set exact stock (positive number, or -1 for unlimited)");
        if (v == null) return;
        const n = parseInt(v, 10);
        if (Number.isNaN(n) || n < -1) return toast.error("Enter a non-negative number or -1");
        setStock(pid, n);
    };
    const toggleTrack = async (pid) => {
        try {
            const { data: fresh } = await api.patch(`/merchant/inventory/${pid}/track`);
            toast.success(fresh.track_stock
                ? "Tracking stock for this item"
                : "Stock ignored — customers can always order");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    return (
        <div data-testid="merchant-inventory">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold inline-flex items-center gap-2">
                        <PackageOpen size={22} /> Inventory
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Set remaining stock per item. When stock reaches zero, customers see <b>Sold out</b> and can&apos;t add it to cart — unless you flip <b>Ignore stock</b>.
                    </p>
                </div>
                <select data-testid="inv-rest" value={rid} onChange={(e) => setRid(e.target.value)} className="bg-card border border-border rounded-xl px-3 py-2 text-sm font-semibold">
                    {rests.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
            </div>

            <div className="grid grid-cols-3 gap-3 mt-6">
                <Stat label="In stock" v={data.in_stock} klass="bg-emerald-100 text-emerald-700" />
                <Stat label="Low stock (≤5)" v={data.low_stock} klass="bg-orange-100 text-orange-700" />
                <Stat label="Sold out" v={data.out_stock} klass="bg-red-100 text-red-700" />
            </div>

            <div className="mt-6 bg-card border border-border rounded-2xl overflow-x-auto">
                <table className="w-full text-sm min-w-[820px]">
                    <thead>
                        <tr className="bg-muted/60 text-left text-[11px] uppercase tracking-wider">
                            <th className="px-4 py-3">Item</th>
                            <th className="px-4 py-3">Price</th>
                            <th className="px-4 py-3">Remaining stock</th>
                            <th className="px-4 py-3">Ignore stock</th>
                            <th className="px-4 py-3">Available</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.items.map((p) => {
                            const trackStock = p.track_stock !== false;        // default ON
                            const isActive = p.status === "ACTIVE";
                            const stockNum = p.stock;
                            const isOutOfStock = trackStock && stockNum != null && stockNum <= 0;
                            const isLow = trackStock && isActive && !isOutOfStock
                                && stockNum != null && stockNum > 0 && stockNum <= 5;
                            return (
                                <tr key={p.id} data-testid={`inv-row-${p.id}`}
                                    className={`border-t border-border ${isOutOfStock ? "bg-red-50/40 dark:bg-red-950/10" : ""}`}>
                                    <td className="px-4 py-3">
                                        <div className="font-semibold inline-flex items-center gap-1">
                                            {p.name}
                                            {isLow && (
                                                <span className="text-[10px] font-bold text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded-full inline-flex items-center gap-0.5">
                                                    <AlertCircle size={9} /> LOW
                                                </span>
                                            )}
                                            {isOutOfStock && (
                                                <span className="text-[10px] font-bold text-red-700 bg-red-100 px-1.5 py-0.5 rounded-full">
                                                    SOLD OUT
                                                </span>
                                            )}
                                            {!trackStock && (
                                                <span className="text-[10px] font-bold text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded-full inline-flex items-center gap-0.5">
                                                    <ShieldOff size={9} /> STOCK IGNORED
                                                </span>
                                            )}
                                        </div>
                                        {p.category_name && (
                                            <p className="text-[11px] text-muted-foreground mt-0.5">{p.category_name}</p>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">{inr(p.selling_price)}</td>
                                    <td className="px-4 py-3">
                                        {trackStock ? (
                                            <div className="inline-flex items-center gap-1">
                                                <button data-testid={`dec-${p.id}`}
                                                    onClick={() => adjust(p.id, -1)}
                                                    disabled={stockNum == null}
                                                    title="Decrease by 1"
                                                    className="w-7 h-7 rounded-lg border border-border disabled:opacity-40"><Minus size={12} className="mx-auto" /></button>
                                                <button data-testid={`set-${p.id}`}
                                                    onClick={() => setExact(p.id)}
                                                    title="Set exact stock"
                                                    className="w-12 text-center font-bold text-base h-7 rounded-lg border border-border hover:bg-muted">
                                                    {stockNum == null
                                                        ? <InfIcon size={14} className="mx-auto text-muted-foreground" />
                                                        : stockNum}
                                                </button>
                                                <button data-testid={`inc-${p.id}`}
                                                    onClick={() => adjust(p.id, 1)}
                                                    disabled={stockNum == null}
                                                    title="Increase by 1"
                                                    className="w-7 h-7 rounded-lg border border-border disabled:opacity-40"><Plus size={12} className="mx-auto" /></button>
                                                <button data-testid={`add10-${p.id}`}
                                                    onClick={() => adjust(p.id, 10)}
                                                    disabled={stockNum == null}
                                                    title="Restock +10"
                                                    className="ml-1 px-2 h-7 rounded-lg border border-border text-[10px] font-bold disabled:opacity-40">+10</button>
                                                <button data-testid={`inf-${p.id}`}
                                                    onClick={() => setStock(p.id, -1)}
                                                    title="Set unlimited"
                                                    className="ml-1 text-[10px] uppercase font-bold text-muted-foreground hover:text-foreground">∞</button>
                                            </div>
                                        ) : (
                                            <span className="text-xs text-muted-foreground italic">— (ignored)</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        <button data-testid={`ignore-${p.id}`}
                                            onClick={() => toggleTrack(p.id)}
                                            title={trackStock
                                                ? "Click to IGNORE stock — customers can always order this item"
                                                : "Click to RESPECT stock — customers can't buy when stock hits 0"}
                                            className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1.5 rounded-lg border ${trackStock
                                                ? "bg-muted text-muted-foreground border-border"
                                                : "bg-violet-50 text-violet-700 border-violet-200"}`}>
                                            {trackStock ? <ShieldCheck size={12} /> : <ShieldOff size={12} />}
                                            {trackStock ? "Respecting" : "Ignored"}
                                        </button>
                                    </td>
                                    <td className="px-4 py-3">
                                        <ProductAvailabilityToggle testid={`tog-${p.id}`} product={p} onChanged={load} />
                                    </td>
                                </tr>
                            );
                        })}
                        {data.items.length === 0 && (
                            <tr>
                                <td colSpan={5} className="text-center py-12 text-muted-foreground inline-flex items-center justify-center gap-2 w-full">
                                    <Package size={20} /> No items in this restaurant yet.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="mt-3 text-[11px] text-muted-foreground">
                💡 <b>Tip</b>: Items with <b>Ignore stock</b> are always orderable — handy for high-volume staples (rice, dal) where you don&apos;t want to keep restocking the count. Items respecting stock auto-decrement on each delivered order.
            </div>
        </div>
    );
}

function Stat({ label, v, klass }) {
    return (
        <div className="bg-card border border-border rounded-2xl p-4">
            <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">{label}</p>
            <p className={`text-2xl font-bold mt-1 inline-block px-3 py-0.5 rounded-lg ${klass}`}>{v}</p>
        </div>
    );
}
